import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderTemplate, type RenderedElement } from './renderer.js';

const DPI = 203;
const MM_PER_INCH = 25.4;

const mmToDots = (mm: number): number => Math.round((mm / MM_PER_INCH) * DPI);

const ptToZplFontSize = (pt: number): { h: number; w: number } => {
  const h = Math.max(10, Math.round((pt / 72) * DPI));
  const w = Math.max(10, Math.round(h * 0.6));
  return { h, w };
};

const escapeZpl = (input: string): string =>
  input.replace(/[\^~\\]/g, '_');

const buildTextBlock = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const pt = el.font_size_pt ?? 10;
  const { h, w } = ptToZplFontSize(pt);
  const rot =
    el.rotation === 90 ? 'R' : el.rotation === 180 ? 'I' : el.rotation === 270 ? 'B' : 'N';
  const font = el.font_weight === 'bold' ? '0' : '0';
  const text = escapeZpl(el.text ?? '');
  return `^FO${x},${y}^A${font}${rot},${h},${w}^FD${text}^FS`;
};

const buildBarcode = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const rot =
    el.rotation === 90 ? 'R' : el.rotation === 180 ? 'I' : el.rotation === 270 ? 'B' : 'N';
  const height = el.h_mm ? mmToDots(el.h_mm) : 60;
  const data = escapeZpl(el.barcode_data ?? '');
  return `^FO${x},${y}^BY2^BC${rot},${height},Y,N,N^FD${data}^FS`;
};

const buildQr = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const size = el.w_mm ? Math.max(1, Math.min(10, Math.round(el.w_mm / 4))) : 4;
  const data = escapeZpl(el.qr_data ?? '');
  return `^FO${x},${y}^BQN,2,${size}^FDQA,${data}^FS`;
};

const buildRect = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const w = mmToDots(el.w_mm ?? 0);
  const h = mmToDots(el.h_mm ?? 0);
  return `^FO${x},${y}^GB${w},${h},2^FS`;
};

const buildLine = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const w = mmToDots(el.w_mm ?? 0);
  const h = Math.max(1, mmToDots(el.h_mm ?? 0.3));
  return `^FO${x},${y}^GB${w},${h},${h}^FS`;
};

export const buildZpl = (
  template: LabelTemplate,
  data: Record<string, unknown>,
  copies: number = 1,
): string => {
  const rendered = renderTemplate(template, data);
  const widthDots = mmToDots(template.width_mm);
  const parts: string[] = [];
  parts.push('^XA');
  parts.push(`^PW${widthDots}`);
  parts.push(`^LL${mmToDots(template.height_mm)}`);
  parts.push('^CI28');
  parts.push(`^PQ${Math.max(1, copies)}`);
  for (const el of rendered) {
    switch (el.type) {
      case 'text':
        parts.push(buildTextBlock(el));
        break;
      case 'barcode':
        parts.push(buildBarcode(el));
        break;
      case 'qr':
        parts.push(buildQr(el));
        break;
      case 'rect':
        parts.push(buildRect(el));
        break;
      case 'line':
        parts.push(buildLine(el));
        break;
      case 'expiry_weekday':
        throw new Error('Il giorno di scadenza richiede la stampa raster: seleziona un driver compatibile.');
      case 'image':
        break;
      default:
        break;
    }
  }
  parts.push('^XZ');
  return parts.join('\n');
};

export class ZplAdapter implements PrinterAdapter {
  readonly driver = 'zpl' as const;

  async discover(): Promise<PrinterDevice[]> {
    // TODO: BLE scan (@capacitor-community/bluetooth-le) filtered on Zebra service UUIDs
    //       and mDNS for TCP printers on port 9100.
    return [];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    // TODO: open BLE GATT notify channel or TCP 9100 socket.
    return { deviceId: device.id, driver: this.driver };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // TODO: close socket / BLE connection.
  }

  async printLabel(
    _session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    const zpl = buildZpl(template, data, copies);
    // TODO: write `zpl` bytes over socket to _session.deviceId.
    //       For BLE: chunk at MTU boundary (20-185 bytes depending on device).
    //       For WiFi: single TCP write to port 9100.
    void zpl;
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    // TODO: send ~HS and parse response.
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
